import React from 'react';
import classNames from 'classnames';
import styles from './ReviewEligibilityCriteria.module.scss';
import CriteriaDescription from './components/CriteriaDescription';
import { Banner, Loading, Text } from 'components/core';
import {
  CriteriaCategory,
  getCategoryKeys,
  getFrameworkCategoriesWithFallbackToPicos,
} from 'concepts/Criteria/framework/CriteriaFramework';
import { useCriteria } from 'hooks/reviews/useCriteria';

// Content only - hosts (toolbar panel, breakout window) own chrome and actions.
type Props = {
  // Heading level under the host's title.
  sectionHeadingComponent?: 'h2' | 'h3';
};

const ReviewEligibilityCriteria = ({
  sectionHeadingComponent = 'h3',
}: Props) => {
  const {
    criteria,
    framework,
    hasCriteria,
    isLoading,
    fetchingFailed,
  } = useCriteria();

  // Only on initial load - a background refetch keeps existing content visible.
  if (isLoading && !criteria) {
    return (
      <Loading
        size="xl"
        className={classNames(styles.LoadingSpinner, styles.centeredState)}
      />
    );
  }

  if (fetchingFailed) {
    return (
      <div className={classNames(styles.container, styles.centeredState)}>
        <Banner type="danger">
          <Text>Unable to load your criteria, please try again later.</Text>
        </Banner>
      </div>
    );
  }

  if (criteria && !hasCriteria) {
    return (
      <Text className={classNames(styles.emptyState, styles.centeredState)}>
        No eligibility criteria have been added to this review.
      </Text>
    );
  }

  if (criteria) {
    const sections = getCategoryKeys(framework);
    const categoryLabels = getFrameworkCategoriesWithFallbackToPicos(framework);

    // We don't want to render the "Other" title if it's the only category present
    const showTitle = Object.keys(criteria).some((key) => {
      const categoryData = criteria[key as CriteriaCategory];
      return (
        key !== 'other' &&
        !!categoryData &&
        (categoryData.include.length || categoryData.exclude.length)
      );
    });

    return (
      <div className={styles.container}>
        {sections.map((section) => {
          const sectionData = criteria[section];

          if (
            !sectionData ||
            (!sectionData.include.length && !sectionData.exclude.length)
          )
            return null;

          const { include, exclude } = sectionData;

          return (
            <section className={styles.section} key={section}>
              {showTitle && (
                <Text
                  component={sectionHeadingComponent}
                  className={styles.sectionTitle}
                >
                  {categoryLabels[section]}
                </Text>
              )}

              <div className={styles.row}>
                <div className={styles.rowItem}>
                  <Text className={styles.include}>Include</Text>
                  <CriteriaDescription
                    description={include}
                    renderAsList={section !== 'other'}
                  />
                </div>
                <div className={styles.rowItem}>
                  <Text className={styles.exclude}>Exclude</Text>
                  <CriteriaDescription
                    description={exclude}
                    renderAsList={section !== 'other'}
                  />
                </div>
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  return null;
};

export default ReviewEligibilityCriteria;
